'use client';

/**
 * Key accuracy heat map.
 *
 * The question this answers is "which keys are letting me down?", which is a
 * question about *polarity* — above or below the accuracy you are aiming for —
 * not about magnitude. So the scale is diverging: warm below the target, cool
 * above it, neutral grey at the midpoint. Untouched keys stay unpainted rather
 * than being coloured as if they were perfect.
 *
 * Colour is never the only signal: every key carries its percentage, and a
 * table view lists the same numbers without the map.
 */
import React, { useMemo, useState } from 'react';
import { Table2, Grid3x3 } from 'lucide-react';
import { KEY_ROWS } from '../lib/keyboard';
import type { KeyHealth } from '../lib/analytics';

/** Accuracy we treat as "solid". Below this a key needs attention. */
const TARGET = 95;

/**
 * Map an accuracy to a colour on the diverging scale. Below TARGET the mark
 * moves toward warm; above it, toward cool. The midpoint is a neutral grey so
 * "exactly on target" reads as neither good nor bad.
 */
function tint(accuracy: number): { background: string; color: string } {
  const spread = Math.min(1, Math.abs(accuracy - TARGET) / (accuracy < TARGET ? 25 : 5));
  const pole = accuracy < TARGET ? 'var(--chart-low)' : 'var(--chart-high)';
  const mix = Math.round(18 + spread * 62);
  return {
    background: `color-mix(in srgb, ${pole} ${mix}%, var(--card))`,
    color: mix > 55 ? '#fff' : 'var(--foreground)',
  };
}

export default function KeyHeatmap({ data }: { data: KeyHealth[] }) {
  const [showTable, setShowTable] = useState(false);
  const byChar = useMemo(() => new Map(data.map((d) => [d.char, d])), [data]);
  const touched = data.filter((d) => d.attempts > 0);

  if (touched.length === 0) {
    return (
      <section className="k-card p-4">
        <h3 className="text-base">Key accuracy map</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Finish a few lessons and every key you have typed will be shaded here.
        </p>
      </section>
    );
  }

  return (
    <section className="k-card p-4">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="text-base">Key accuracy map</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Warm keys are below {TARGET}% and need practice. Grey keys have not been typed yet.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="k-btn k-btn-ghost k-btn-sm shrink-0"
          aria-pressed={showTable}
        >
          {showTable ? <Grid3x3 size={15} /> : <Table2 size={15} />}
          <span className="sr-only sm:not-sr-only">{showTable ? 'Map' : 'Table'}</span>
        </button>
      </div>

      {showTable ? (
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {['Key', 'Accuracy', 'Times typed', 'Finger'].map((h) => (
                  <th key={h} className="k-eyebrow text-left py-2 pr-4">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {touched.map((key) => (
                <tr key={key.char} style={{ borderTop: '1px solid var(--border)' }}>
                  <td className="py-1.5 pr-4 font-mono font-bold">{key.label}</td>
                  <td className="py-1.5 pr-4 font-bold tabular-nums">{key.accuracy}%</td>
                  <td className="py-1.5 pr-4 font-bold tabular-nums">{key.attempts}</td>
                  <td className="py-1.5 pr-4 text-muted-foreground">{key.fingerLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-1 mt-3 overflow-x-auto pb-1">
            {KEY_ROWS.slice(0, 4).map((row, rowIndex) => (
              <div className="k-kbd-row" key={`heat-row-${rowIndex}`}>
                {row
                  .filter((def) => def.id.length === 1)
                  .map((def) => {
                    const stat = byChar.get(def.id);
                    const style =
                      stat && stat.attempts > 0
                        ? tint(stat.accuracy)
                        : {
                            background: 'var(--muted)',
                            color: 'var(--faint)',
                          };
                    return (
                      <div
                        key={def.id}
                        className="k-key"
                        style={{
                          ...style,
                          borderColor: 'var(--border)',
                          boxShadow: 'none',
                          ['--k-key-size' as string]: 'clamp(30px, 3.6vw, 46px)',
                          flexGrow: 1,
                          flexBasis: 0,
                        }}
                        title={
                          stat && stat.attempts > 0
                            ? `${def.label}: ${stat.accuracy}% over ${stat.attempts} presses (${stat.fingerLabel})`
                            : `${def.label}: not typed yet`
                        }
                      >
                        <span className="flex flex-col items-center leading-none gap-[1px]">
                          <span>{def.label}</span>
                          {stat && stat.attempts > 0 && (
                            <span
                              style={{ fontSize: '0.58em', opacity: 0.9 }}
                              className="tabular-nums"
                            >
                              {stat.accuracy}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-xs font-bold text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block rounded"
                style={{ width: 14, height: 14, background: 'var(--chart-low)' }}
                aria-hidden="true"
              />
              Below {TARGET}%
            </span>
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block rounded"
                style={{ width: 14, height: 14, background: 'var(--chart-mid)' }}
                aria-hidden="true"
              />
              Around {TARGET}%
            </span>
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block rounded"
                style={{ width: 14, height: 14, background: 'var(--chart-high)' }}
                aria-hidden="true"
              />
              Above {TARGET}%
            </span>
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block rounded"
                style={{
                  width: 14,
                  height: 14,
                  background: 'var(--muted)',
                  border: '1px solid var(--border)',
                }}
                aria-hidden="true"
              />
              Not typed yet
            </span>
          </div>
        </>
      )}
    </section>
  );
}
